'use client'

import React from 'react'
import Link from 'next/link'
import { Button } from '@/ui/atoms/Button'
import { LockSvg, DownloadSvg } from '@/ui/components/KnowledgeHubV2/icons'
import { useDownloadGate } from '@/lib/downloads/useDownloadGate'

/**
 * Member-aware parts of the KnowledgeDownloads panel. The panel itself is a server
 * component and cannot see the Supabase session, so the heading, footer and tool rows
 * read it here (via useDownloadGate). Before 2026-09-17 the panel hardcoded a
 * signed-out state, so signed-in members still saw "Sign in to download".
 */

type HeadingProps = {
  hasFiles: boolean
  hasGatedTools: boolean
  hasTools: boolean
  heading: string
}

export const PanelHeading: React.FC<HeadingProps> = ({
  hasFiles,
  hasGatedTools,
  hasTools,
  heading,
}) => {
  const { user } = useDownloadGate()
  const locked = !user && (hasFiles || hasGatedTools)

  return (
    <div className="flex items-center gap-2 text-text-base text-body-S-semibold">
      {locked ? <LockSvg className="w-4 h-4" /> : <DownloadSvg className="w-4 h-4" />}
      {locked
        ? hasTools
          ? 'Sign in to access'
          : 'Sign in to download'
        : hasTools && hasFiles
          ? 'Downloads & tools'
          : heading}
    </div>
  )
}

export const PanelFooter: React.FC<{ hasFiles: boolean; hasGatedTools: boolean }> = ({
  hasFiles,
  hasGatedTools,
}) => {
  const { user, loginHref, trackGateClick } = useDownloadGate()
  if (user || !(hasFiles || hasGatedTools)) return null

  return (
    <p className="text-text-subtle text-body-XS-regular">
      Free account required. Already have one?{' '}
      <Link
        href={loginHref}
        className="underline"
        onClick={() => trackGateClick('Downloads panel: Sign in', 'downloads_panel')}
      >
        Sign in
      </Link>
      .
    </p>
  )
}

export const ToolButton: React.FC<{ url: string; requiresAuth: boolean; label?: string }> = ({
  url,
  requiresAuth,
  label,
}) => {
  const { user, loginHref, trackGateClick, notifyToolOpen } = useDownloadGate()

  if (requiresAuth && !user) {
    return (
      <Button variant="primary" size="sm" asChild>
        <Link href={loginHref} onClick={() => trackGateClick(url, 'tool_button')}>
          <LockSvg className="w-4 h-4" />
          Sign in to open
        </Link>
      </Button>
    )
  }

  return (
    <Button variant="primary" size="sm" asChild>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={() => user && notifyToolOpen(label || url, url)}
      >
        Open the tool ↗
      </a>
    </Button>
  )
}
